/**
 * Redstone power rules, as functions of block ids (vanilla's
 * getSignal / getDirectSignal / isRedstoneConductor):
 *
 *  - a component gives WEAK power to a neighbouring cell (`emitWeak`) and may
 *    give STRONG power into it (`emitStrong`);
 *  - a conductor (opaque full cube) that is strongly powered passes that on to
 *    all its neighbours; dust powers the block it runs into and the one under
 *    it, but other dust never reads power back out of such a block;
 *  - dust carries 0..15 and loses one per cell.
 *
 * Directions are blocks.ts face indices; `d ^ 1` is the opposite.
 */
import {
  BLOCKS, FACE_DIRS, IS_OPAQUE, IS_SOLID, RAIL_KIND, REDSTONE_BLOCK_ID, RENDER_SHAPE, RenderShape,
} from '../../blocks';
import { K, RS_DIR, RS_KIND, RS_POW, RS_VAR } from './defs';

/** Anything blocks can be read from. */
export interface BlockReader {
  getBlock(x: number, y: number, z: number): number;
}

export const DIRS = FACE_DIRS;
/** The four horizontal faces. */
export const HORIZ: readonly number[] = [0, 1, 4, 5];

/** Opaque full cubes carry power; glowing and mechanical ones (redstone block, glowstone, observer, piston) do not. */
export const IS_CONDUCTOR = new Uint8Array(4096);
for (const b of BLOCKS) {
  if (!b) continue;
  const k = RS_KIND[b.id];
  const ok = IS_OPAQUE[b.id] === 1 && IS_SOLID[b.id] === 1 && RENDER_SHAPE[b.id] === RenderShape.Cube
    && b.id !== REDSTONE_BLOCK_ID && b.light === 0 && k !== K.OBSERVER && k !== K.PISTON && !/Leaves|Glass|Ice/.test(b.name);
  IS_CONDUCTOR[b.id] = ok || k === K.LAMP ? 1 : 0;
}

/** Weak power the block `id` at (x, y, z) gives the neighbour in direction `d`. */
export function emitWeak(w: BlockReader, x: number, y: number, z: number, id: number, d: number, wires = true): number {
  const k = RS_KIND[id];
  if (k === K.NONE) {
    if (id === REDSTONE_BLOCK_ID) return 15;
    return RAIL_KIND[id] === 4 ? 15 : 0; // a detector rail with a cart on it
  }
  const p = RS_POW[id];
  if (p === 0) return 0;
  switch (k) {
    case K.DUST:
      if (!wires || d === 2) return 0;
      return d === 3 || (dustSides(w, x, y, z) & (1 << d)) !== 0 ? p : 0;
    case K.TORCH: return d === RS_DIR[id] ? 0 : 15;
    case K.LEVER: case K.BUTTON: case K.HOOK: return 15;
    case K.PLATE: case K.TARGET: case K.DAYLIGHT: return p;
    case K.REPEATER: return d === RS_DIR[id] ? 15 : 0;
    case K.COMPARATOR: return d === RS_DIR[id] ? p : 0;
    case K.OBSERVER: return d === (RS_DIR[id] ^ 1) ? 15 : 0;
    default: return 0;
  }
}

/** Strong power the block gives INTO the neighbour in direction `d` (a conductor there passes it on). */
export function emitStrong(w: BlockReader, x: number, y: number, z: number, id: number, d: number, wires = true): number {
  const k = RS_KIND[id];
  if (k === K.NONE) return RAIL_KIND[id] === 4 && d === 3 ? 15 : 0;
  const p = RS_POW[id];
  if (p === 0) return 0;
  switch (k) {
    case K.DUST: return emitWeak(w, x, y, z, id, d, wires);
    case K.TORCH: return d === 2 ? 15 : 0;
    case K.LEVER: case K.BUTTON: case K.HOOK: return d === RS_DIR[id] ? 15 : 0;
    case K.PLATE: return d === 3 ? p : 0;
    case K.REPEATER: case K.COMPARATOR: case K.OBSERVER: return emitWeak(w, x, y, z, id, d);
    default: return 0;
  }
}

/** How strongly the block at (x, y, z) is powered from outside (what a conductor there hands on). */
export function strongInto(w: BlockReader, x: number, y: number, z: number, wires = true): number {
  let best = 0;
  for (let d = 0; d < 6; d++) {
    const v = DIRS[d];
    const nx = x + v[0], ny = y + v[1], nz = z + v[2];
    const p = emitStrong(w, nx, ny, nz, w.getBlock(nx, ny, nz), d ^ 1, wires);
    if (p > best) { best = p; if (best >= 15) break; }
  }
  return best;
}

/** The signal the cell at (x, y, z) gets from its neighbour in direction `d` (looking from the cell). */
export function signalFrom(w: BlockReader, x: number, y: number, z: number, d: number, wires = true): number {
  const v = DIRS[d];
  const nx = x + v[0], ny = y + v[1], nz = z + v[2];
  const id = w.getBlock(nx, ny, nz);
  const p = emitWeak(w, nx, ny, nz, id, d ^ 1, wires);
  if (p >= 15 || IS_CONDUCTOR[id] === 0) return p;
  return Math.max(p, strongInto(w, nx, ny, nz, wires));
}

/** Strongest signal the cell receives from its six neighbours (`skip` leaves one direction out). */
export function receivedPower(w: BlockReader, x: number, y: number, z: number, wires = true, skip = -1): number {
  let best = 0;
  for (let d = 0; d < 6; d++) {
    if (d === skip) continue;
    const p = signalFrom(w, x, y, z, d, wires);
    if (p > best) { best = p; if (best >= 15) break; }
  }
  return best;
}

/** Would dust beside this block point at it? `d` is the direction from the dust to the block. */
function dustJoins(id: number, d: number): boolean {
  const k = RS_KIND[id];
  switch (k) {
    case K.NONE: return id === REDSTONE_BLOCK_ID || RAIL_KIND[id] === 3 || RAIL_KIND[id] === 4;
    case K.DUST: case K.TORCH: case K.LEVER: case K.BUTTON: case K.PLATE: case K.COMPARATOR: case K.TARGET: case K.DAYLIGHT: case K.HOOK: return true;
    case K.REPEATER: return RS_DIR[id] === d || RS_DIR[id] === (d ^ 1);
    case K.OBSERVER: return RS_DIR[id] === d;
    default: return false;
  }
}

/**
 * Where the dust at (x, y, z) points: bits 0, 1, 4, 5 = it runs towards that
 * side (and powers what is there); bits 8 + face = it also climbs the block on
 * that side. Vanilla's rules: a lone connection becomes a straight line, no
 * connection at all a cross.
 */
export function dustSides(w: BlockReader, x: number, y: number, z: number): number {
  const coveredAbove = IS_CONDUCTOR[w.getBlock(x, y + 1, z)] === 1;
  let mask = 0, n = 0;
  for (const d of HORIZ) {
    const v = DIRS[d];
    const side = w.getBlock(x + v[0], y, z + v[2]);
    let joins = dustJoins(side, d);
    if (!coveredAbove && IS_SOLID[side] === 1 && RS_KIND[w.getBlock(x + v[0], y + 1, z + v[2])] === K.DUST) { joins = true; mask |= 1 << (8 + d); }
    if (!joins && IS_CONDUCTOR[side] === 0 && RS_KIND[w.getBlock(x + v[0], y - 1, z + v[2])] === K.DUST) joins = true;
    if (joins) { mask |= 1 << d; n++; }
  }
  if (n === 0) return mask | 0b110011;
  if (n === 1) for (const d of HORIZ) if (mask & (1 << d)) mask |= 1 << (d ^ 1);
  return mask;
}

/** The dust cells the dust at (x, y, z) exchanges power with (level, up a block, down a block): calls `fn` for each. */
export function eachDustLink(w: BlockReader, x: number, y: number, z: number, fn: (x: number, y: number, z: number, id: number) => void): void {
  const coveredAbove = IS_CONDUCTOR[w.getBlock(x, y + 1, z)] === 1;
  for (const d of HORIZ) {
    const v = DIRS[d];
    const sx = x + v[0], sz = z + v[2];
    const side = w.getBlock(sx, y, sz);
    if (RS_KIND[side] === K.DUST) { fn(sx, y, sz, side); continue; }
    if (IS_CONDUCTOR[side] === 1) {
      if (!coveredAbove) { const up = w.getBlock(sx, y + 1, sz); if (RS_KIND[up] === K.DUST) fn(sx, y + 1, sz, up); }
    } else {
      const dn = w.getBlock(sx, y - 1, sz);
      if (RS_KIND[dn] === K.DUST) fn(sx, y - 1, sz, dn);
    }
  }
}

/** What a repeater / comparator pointing along `dir` reads from behind it (dust counts whichever way it runs). */
export function diodeInput(w: BlockReader, x: number, y: number, z: number, dir: number): number {
  const back = dir ^ 1, v = DIRS[back];
  const p = signalFrom(w, x, y, z, back);
  if (p >= 15) return p;
  const id = w.getBlock(x + v[0], y + v[1], z + v[2]);
  return RS_KIND[id] === K.DUST ? Math.max(p, RS_POW[id]) : p;
}

/** The two sides of a horizontal direction. */
export const sidesOf = (dir: number): readonly [number, number] => (dir >> 1 === 0 ? [4, 5] : [0, 1]);

/** Note block instrument (0 harp .. 15 pling) by the block underneath, as vanilla's NoteBlockInstrument. */
const INSTRUMENTS: ReadonlyArray<readonly [number, RegExp]> = [
  [5, /Block of Gold/], [6, /Clay/], [7, /Packed Ice/], [8, /Wool/], [9, /Bone/], [10, /Block of Iron/], [11, /Soul Sand/], [12, /Pumpkin/],
  [13, /Block of Emerald/], [14, /Hay/], [15, /Glowstone/], [3, /Glass|Sea Lantern|Beacon/], [2, /Sand|Gravel|Concrete Powder/],
  [1, /Planks|Log|Wood|Stem|Chest|Crafting|Bookshelf|Note Block|Fence|Barrel|Composter|Lectern/],
  [4, /Stone|Cobble|Brick|Ore|Obsidian|Netherrack|Deepslate|Andesite|Diorite|Granite|Basalt|Blackstone|Quartz|Terracotta|Concrete|Bedrock|Prismarine|Tuff|Furnace|Observer/],
];
const INSTRUMENT = new Uint8Array(4096);
for (const b of BLOCKS) if (b) for (const [n, re] of INSTRUMENTS) if (re.test(b.name)) { INSTRUMENT[b.id] = n; break; }
/** The `d` of a 'note' fx: pitch (0..24) | instrument << 8. */
export function noteData(w: BlockReader, x: number, y: number, z: number, id: number): number {
  return RS_VAR[id] | (INSTRUMENT[w.getBlock(x, y - 1, z)] << 8);
}
